import { cloneDeep } from 'lodash';
import reviewsService from '../services/reviewsService';

const initialState = {
  ratings: {},
  activeFilters: {
    platform: '0',
    sorting: 'orderBy=publishedAt&orderDir=DESC',
    gmbSorting: 'most-recent',
    count: 8,
    page: 1,
    department: 'overview',
  },
  totalReviews: 0,
  totalDisplayedReviews: 0,
  displayedReviews: [],
  adSlots: [2, 7], // 2 and 7 gives a repeating pattern of 4 reviews, 1 ad
  stillMoreReviews: true,
  isLoadingReviews: false,
  GMBReviewsIsEnabled: null,
  // From business module
  // Business reviews
  businessReviews: null,
};

const mutations = {
  SET_ACTIVE_PLATFORM_FILTER(state, value) {
    state.activeFilters.platform = value;
  },
  SET_ACTIVE_DEPARTMENT_FILTER(state, value) {
    state.activeFilters.department = value;
  },
  SET_ACTIVE_SORTING_FILTER(state, value) {
    state.activeFilters.sorting = value;
  },
  SET_ACTIVE_GMB_SORTING_FILTER(state, value) {
    state.activeFilters.gmbSorting = value;
  },
  SET_REVIEWS(state, value) {
    state.displayedReviews = state.displayedReviews.concat(value);
  },
  SET_DEPARTMENT_BREAKDOWN(state, value) {
    state.departmentBreakdown = value;
  },
  SET_DISCARD_REVIEWS(state) {
    state.displayedReviews = [];
  },
  SET_NEXT_PAGE(state) {
    state.activeFilters.page += 1;
  },
  SET_PAGE_RESET(state) {
    state.activeFilters.page = 1;
    state.totalReviews = 0;
    state.totalDisplayedReviews = 0;
  },
  SET_NUMBER_OF_TOTAL_REVIEWS(state, value) {
    state.totalReviews = value;
  },
  SET_NUMBER_OF_TOTAL_REVIEWS_DISPLAYED(state, value) {
    state.totalDisplayedReviews = value;
  },
  SET_ALL_FILTERS(state, value) {
    state.activeFilters = value;
  },
  SET_IF_STILL_MORE_REVIEWS(state, value) {
    state.stillMoreReviews = value;
  },
  SET_LOADING_REVIEWS(state, data) {
    state.isLoadingReviews = data;
  },
  SET_BUSINESS_REVIEWS(state, data) {
    state.businessReviews = data;
  },
  SET_GMB_REVIEW_IS_ENABLED(state, data) {
    state.GMBReviewsIsEnabled = data;
  },
};
const actions = {
  fetchReviews({ state, rootState, getters, commit }) {
    commit('SET_LOADING_REVIEWS', true);
    // eslint-disable-next-line camelcase
    const { key, external_id } = rootState.route.query;
    // GMB reviews
    if (state.businessReviews && state.businessReviews.gmbreviews) {
      return reviewsService
        .getGMBReviews({
          profileId: rootState.business.info.gmbProfileId,
          department: getters.getActiveFilters.department,
          page: getters.getActiveFilters.page,
          sort: getters.getActiveFilters.gmbSorting,
        })
        .then(res => {
          const data = cloneDeep(res.data);
          // Find total number of reviews
          commit('SET_NUMBER_OF_TOTAL_REVIEWS', data.payload.reviewCount);
          commit(
            'SET_NUMBER_OF_TOTAL_REVIEWS_DISPLAYED',
            state.activeFilters.count * (state.activeFilters.page - 1) +
              (data.payload.reviews ? data.payload.reviews.length : 0)
          );
          if (getters.getTotalDisplayedReviews < getters.getTotalReviews) {
            commit('SET_IF_STILL_MORE_REVIEWS', true);
          } else {
            commit('SET_IF_STILL_MORE_REVIEWS', false);
          }

          if (rootState.business.info.products.length > 1) {
            if (data.payload.reviews) {
              state.adSlots.forEach(slot => {
                data.payload.reviews.splice(slot, 0, null);
              });
            }
          }

          commit('SET_REVIEWS', data.payload.reviews);
          if (data.payload.star_breakdown) {
            commit('SET_DEPARTMENT_BREAKDOWN', data.payload.star_breakdown);
          }
          commit('SET_LOADING_REVIEWS', false);
        })
        .catch(error => {
          this._vm.$log('Error in fetching gmb reviews', error);
        });
    }
    // Old reviews
    return reviewsService
      .getReviews({
        apiKey: key,
        externalId: external_id,
        reviewQueryStrings: getters.getQueryString,
      })
      .then(res => {
        const data = cloneDeep(res.data);
        // TO DO - use more reliable way to check for more reviews
        if (data.length < getters.getActiveFilters.count) {
          commit('SET_IF_STILL_MORE_REVIEWS', false);
        } else {
          commit('SET_IF_STILL_MORE_REVIEWS', true);
        }
        if (rootState.business.info.products.length > 1) {
          state.adSlots.forEach(slot => {
            data.splice(slot, 0, null);
          });
        }
        commit('SET_REVIEWS', data);
        commit('SET_LOADING_REVIEWS', false);
      });
  },
  loadMore({ commit, dispatch }) {
    commit('SET_NEXT_PAGE');
    dispatch('fetchReviews');
  },
  reset({ commit }) {
    commit('SET_DISCARD_REVIEWS');
    commit('SET_PAGE_RESET');
  },
  replaceFilters({ commit }, value) {
    commit('SET_ALL_FILTERS', value);
  },
  updateActivePlatformFilter({ commit }, data) {
    commit('SET_ACTIVE_PLATFORM_FILTER', data);
  },
  updateActiveDepartmentFilter({ commit }, data) {
    commit('SET_ACTIVE_DEPARTMENT_FILTER', data);
  },
  updateActiveSortingFilter({ commit }, data) {
    commit('SET_ACTIVE_SORTING_FILTER', data);
  },
  updateActiveGmbSortingFilter({ commit }, data) {
    commit('SET_ACTIVE_GMB_SORTING_FILTER', data);
  },
  async getGMBReviewInfo({ rootState, commit }) {
    try {
      const reviewResponse = await reviewsService.getGMBReviewInfo(rootState.business.info.gmbProfileId);
      reviewResponse.data.gmbreviews = true;
      commit('SET_BUSINESS_REVIEWS', reviewResponse.data);
      commit('SET_ACTIVE_GMB_SORTING_FILTER', rootState.business.info.gmbSortMethod);
    } catch (error) {
      this._vm.$log('Error in fetching gmb reviews', error);
      commit('business/HIDE_PRODUCT', { products: ['reviews'] }, { root: true });
    }
  },
  async getReviewInfo({ rootState, commit }) {
    try {
      const reviewResponse = await reviewsService.getReviewInfo(
        rootState.business.api.key,
        rootState.business.api.externalID
      );
      commit('SET_BUSINESS_REVIEWS', reviewResponse.data);
    } catch (error) {
      this._vm.$log('Error in fetching reviews', error);
      commit('business/HIDE_PRODUCT', { products: ['reviews'] }, { root: true });
    }
  },
};
const getters = {
  getDisplayedReviews: state => state.displayedReviews,
  getActiveFilters: state => state.activeFilters,
  getTotalReviews: state => state.totalReviews,
  getTotalDisplayedReviews: state => state.totalDisplayedReviews,
  getAdSlots: state => state.adSlots,
  getStillMoreReviews: state => state.stillMoreReviews,
  getLoadingReviewsBoolean: state => state.isLoadingReviews,
  // eslint-disable-next-line no-shadow
  getQueryString: (state, getters) => {
    // linter complains about getters, but it's required.
    const platformString =
      getters.getActiveFilters.platform === '0' ? 'outlets=1,2,3' : `outlets=${getters.getActiveFilters.platform}`;
    return `?page=${getters.getActiveFilters.page}&count=${getters.getActiveFilters.count}&${getters.getActiveFilters.sorting}&${platformString}`;
  },
  businessReviewId: state => (state.businessReviews ? state.businessReviews.id : null),
  businessReviewOverallRating: state => {
    if (state.businessReviews && state.businessReviews.aggregateScores) {
      return state.businessReviews.aggregateScores.combinedRating;
    }
    if (state.businessReviews && state.businessReviews.ratings) {
      return parseFloat(state.businessReviews.ratings.overview.rating);
    }
    return 0;
  },
  businessReviewOverallRatingCount: state => {
    if (state.businessReviews && state.businessReviews.aggregateScores) {
      return state.businessReviews.aggregateScores.totalReviewCount;
    }
    if (state.businessReviews && state.businessReviews.ratings) {
      return parseFloat(state.businessReviews.ratings.overview.reviewCount);
    }
    return 0;
  },
  businessReviewAggregateScores: state =>
    state.businessReviews && state.businessReviews.aggregateScores
      ? state.businessReviews.aggregateScores.aggregate_scores
      : [],
  isGmbReviews: state =>
    state.businessReviews && state.businessReviews.gmbreviews ? state.businessReviews.gmbreviews : false,
  businessReviewRatings: state =>
    state.businessReviews && state.businessReviews.ratings ? state.businessReviews.ratings : {},
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  mutations,
  actions,
  getters,
};
